import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { DataTable, columnTypes, pitchTypeDisplayName, number, ratio } from 'yankees-react-utils';
import ReportCard from '../../../../components/ReportCard';
import StatTooltip from '../../../../components/StatTooltip';

const PitchUsageWrapper = styled.div`
    .rcma-header {
        text-align: center;
        h5 {
            color: #002255;
        }
    }
    hr {
        border-top-color: #333;
    }
    .table {
        .highlight {
            background-color: #FFFF77 !important;
        }
        .highlight, .high-usage, .low-usage, .border-bottom-3 {
            font-weight: bold !important;
        }
        .high-usage {
            background-color: #AACCFF !important;
        }
        .low-usage {
            background-color: #F2B9B4 !important;
        }
        .border-right-2 {
            border-right: 2px solid #333 !important;
        }
        .border-bottom-3 {
            border-bottom: 3px solid #333 !important;
        }
    }
`;

const getUsageClass = (pPct, lgPct, threshold, includeLowUsage) => {
    let usageClass;
    if (pPct >= (lgPct + threshold)) {
        usageClass = 'high-usage';
    } else if (includeLowUsage && pPct <= (lgPct - threshold)) {
        usageClass = 'low-usage';
    }
    return usageClass;
};

const kzoneAccuracy = (lgavgkzon, stat, kAccuracy, lgavgKey) => {
    if (stat.pitches / stat.pitches_total < 0.1) {
        return '';
    }
    let accuracyClass = '';
    const lgAvg = lgavgkzon.find((avg) => avg.fxtype === stat.fxtype || (avg.fxtype === null && !stat.fxtype));
    if (kAccuracy > 0 && lgAvg && lgAvg[`t${lgavgKey}`] > 0) {
        const pEst = lgAvg[`kzon${lgavgKey}`] / lgAvg[`t${lgavgKey}`];
        const stdDev = Math.sqrt(pEst * (1 - pEst) / 200);
        accuracyClass = getUsageClass(kAccuracy, pEst, 2 * stdDev, true);
    }
    return accuracyClass;
};

const pitchFreq = (lgavgkzon, numFbInRepertoire, stat, pitchUsage, statKey, lgavgKey) => {
    if (stat.pitches / stat.pitches_total < 0.1) {
        return '';
    }

    let usageClass = '';
    if (!pitchUsage) return usageClass;

    const lookupKey = lgavgKey === undefined ? statKey : lgavgKey;
    if (!stat.isfastball) {
        const pitchTypeUsageAll = stat.pitches / (stat.pitches_total || stat.totalpitches);
        if (pitchUsage > (pitchTypeUsageAll + 0.10)) {
            usageClass = 'high-usage';
        }
    } else if (stat.isfastball && statKey === 'cnt32' && pitchUsage >= 0.945) {
        usageClass = 'high-usage';
    } else if (stat.isfastball) {
        const fxtype = numFbInRepertoire === 1 ? 1 : stat.fxtype;
        if (fxtype === 1) {
            const lgAvgCt = lgavgkzon.find((avg) => avg.fxtype === fxtype);
            const lgAvgAll = lgavgkzon.find((avg) => avg.fxtype === null);
            if (lgAvgCt && lgAvgAll && lgAvgAll[`t${lookupKey}`] > 0) {
                usageClass = getUsageClass(pitchUsage, lgAvgCt[`t${lookupKey}`] / lgAvgAll[`t${lookupKey}`], 0.10);
            }
        }
    }
    return usageClass;
};

const getOverviewTableColumns = (translate, lgavgkzon, numFbInRepertoire) => [{
    id: 'pitchtype1',
    title: translate('sections.opponent.player.type'),
    type: columnTypes.TEXT,
    cellClass: ({ highlight }) => highlight && 'highlight',
    isSticky: true,
    transform: (row) => pitchTypeDisplayName(row, true, true, translate),
}, {
    id: 'avgvelo',
    title: translate('sections.opponent.player.velo-range'),
    type: columnTypes.TEXT,
    cellClass: 'text-nowrap',
    transform: (row) => {
        let range = `${number(row.minvelo) || '-'}-${number(row.maxvelo) || '-'}`;
        if (row.velopitches) { range += ` (${number(row.avgvelo) || '--'})`; }
        return range;
    },
}, {
    id: 'pitches',
    title: '%',
    type: columnTypes.PERCENT,
    cellClass: (r, v) => pitchFreq(lgavgkzon, numFbInRepertoire, r, v, 'pitches', 'pit'),
    transform: (row) => ratio(row.pitches, row.pitches_total),
}, {
    id: 'inzone',
    title: translate('baseball.abbr-strikezone'),
    type: columnTypes.PERCENT,
    statistic: 'Kzon',
    cellClass: (r, v) => kzoneAccuracy(lgavgkzon, r, v, 'pit'),
    transform: (row) => ratio(row.pstrike, row.pitches),
}, {
    id: 'firstpit',
    title: '0-0',
    type: columnTypes.PERCENT,
    cellClass: (r, v) => pitchFreq(lgavgkzon, numFbInRepertoire, r, v, 'firstpit', '00'),
    transform: (row) => ratio(row.firstpit, row.firstpit_total),
}, {
    id: 'ahead',
    title: translate('sections.opponent.player.abbr-hitter-ahead'),
    type: columnTypes.PERCENT,
    cellClass: (r, v) => pitchFreq(lgavgkzon, numFbInRepertoire, r, v, 'ahead'),
    transform: (row) => ratio(row.ahead, row.ahead_total),
}, {
    id: 'twostrikes',
    title: '2K',
    type: columnTypes.PERCENT,
    cellClass: (r, v) => pitchFreq(lgavgkzon, numFbInRepertoire, r, v, 'twostrikes', '2k'),
    transform: (row) => ratio(row.twostrikes, row.twostrikes_total),
}, {
    id: 'cnt32',
    title: '3-2',
    type: columnTypes.PERCENT,
    cellClass: (r, v) => pitchFreq(lgavgkzon, numFbInRepertoire, r, v, 'cnt32', '32'),
    transform: (row) => ratio(row.cnt32, row.cnt32_total),
}, {
    id: 'risp',
    title: translate('baseball.abbr-runners-in-position'),
    type: columnTypes.PERCENT,
    cellClass: (r, v) => pitchFreq(lgavgkzon, numFbInRepertoire, r, v, 'risp'),
    headerClass: 'highlight',
    transform: (row) => ratio(row.risp, row.risp_total),
}, {
    id: 'risp00',
    title: `0-0 ${translate('baseball.abbr-runners-in-position')}`,
    type: columnTypes.PERCENT,
    cellClass: (r, v) => pitchFreq(lgavgkzon, numFbInRepertoire, r, v, 'risp00'),
    headerClass: 'highlight',
    transform: (row) => ratio(row.risp00, row.risp00_total),
}];

const PitchUsage = ({ isLhb, repertoire, lgavgkzon }) => {
    const { t: translate } = useTranslation();

    return (
        <PitchUsageWrapper>
            <ReportCard
                title={`${translate('sections.opponent.player.pitch-usage')} ${translate('baseball.abbr-versus')} 
                ${isLhb ? translate('baseball.lhh') : translate('baseball.rhh')}`}
            >
                { repertoire.filter((x) => x.pitches_total).length > 0
                    ? (
                        <DataTable
                            columns={getOverviewTableColumns(translate, lgavgkzon, repertoire
                                .filter((r) => r.pitches_total !== null && r.pitches_total > 0)
                                .reduce((acc, r) => acc + (r.isfastball ? 1 : 0), 0))}
                            rows={repertoire.filter((x) => x.pitches_total)}
                            statTooltip={StatTooltip}
                        />
                    ) : (<>{translate('sections.opponent.player.not-enough-data')}</>)}
            </ReportCard>
        </PitchUsageWrapper>
    );
};

export default PitchUsage;
